"use client";

import React, { useEffect, useRef, useState } from "react";
import Image, { StaticImageData } from "next/image";

// --- Types ---

export type BenefitItem = {
  icon: StaticImageData | string | undefined;
  title: string[] | string;
  description: string;
};

export type IndustryBenefitCardProps = {
  item: BenefitItem;
  index: number;
  isExpanded: boolean;
  // eslint-disable-next-line no-unused-vars
  onToggle: (_e: React.MouseEvent) => void;
  cardRef?: React.Ref<HTMLElement> | undefined;
  onClick: () => void;
};

export default function IndustryBenefitCard({
  item,
  index,
  isExpanded,
  onToggle,
  cardRef,
  onClick,
}: IndustryBenefitCardProps) {
  const [showReadMore, setShowReadMore] = useState(false);
  const textRef = useRef<HTMLParagraphElement>(null);

  // --- Overflow Detection (Robust) ---
  useEffect(() => {
    const el = textRef.current;
    if (!el) return;

    const checkOverflow = () => {
      setShowReadMore(el.scrollHeight > el.clientHeight);
    };

    checkOverflow();

    window.addEventListener("resize", checkOverflow);
    return () => window.removeEventListener("resize", checkOverflow);
  }, [item.description]);

  // --- Normalize Title ---
  const title = Array.isArray(item.title) ? item.title.join(" ") : item.title;

  return (
    <article
      ref={cardRef}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter") onClick();
      }}
      // className="flex-[0_0_100%] sm:flex-[0_0_100%] lg:flex-[0_0_50%] xl:flex-[0_0_24%] 2xl:flex-[0_0_24%] text-white/90 lg:cursor-pointer group flex flex-col self-start snap-start px-5 lg:px-0 outline-none"
      className="fw-full text-white/90 lg:cursor-pointer group flex flex-col self-start snap-start px-5 outline-none"
    >
      {/* 1. Header Index */}
      <div className="flex flex-col gap-5 shrink-0">
        <div className="text-white/80 text-[18px] clash-font tracking-widest">
          {String(index + 1).padStart(2, "0")}
        </div>
        <div className="h-px w-full bg-white/10 group-hover:bg-white/30 transition-colors duration-300"></div>
      </div>

      {/* 2. Icon */}
      <div className="mt-8 shrink-0">
        <Image
          src={item.icon ?? "/placeholder-icon.png"}
          alt={`${title} icon`}
          width={50}
          height={50}
          className="w-[50px] h-[50px] object-contain"
        />
      </div>

      {/* 3. Title */}
      <div className="min-h-[120px] sm:min-h-[160px] flex flex-col justify-end">
        <h3 className="clash-font text-white text-[length:var(--space-20-32)] font-[500] leading-tight">
          {title}
        </h3>
      </div>

      {/* 4. Description */}
      <div className="mt-[20px] relative">
        <p
          ref={textRef}
          className={`text-white/70 leading-relaxed text-[length:var(--primarybtn)] transition-all duration-500 ease-in-out ${
            !isExpanded ? "line-clamp-3" : ""
          }`}
          dangerouslySetInnerHTML={{ __html: item.description }}
        />

        {showReadMore && (
          <button
            onClick={(e) => {
              e.stopPropagation(); // 🔥 Fix bubbling issue
              onToggle(e);
            }}
            className="mt-2 inline-flex items-center justify-center p-1 rounded-md text-[#225bd4] hover:bg-[#225bd4] hover:text-white transition-all"
            aria-label={isExpanded ? "Read less" : "Read more"}
          >
            {isExpanded ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="m18 15-6-6-6 6" />
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="m6 9 6 6 6-6" />
              </svg>
            )}
          </button>
        )}
      </div>
    </article>
  );
}
